import { colors } from '@karnott/colors';
import { Characteristic } from '../components/task_types/modals/EditTaskTypeModal';
import {
  PHYTO_TASK_TYPE,
  TASK_METADATA_QUESTION_FORMAT,
  TASK_METADATA_QUESTION_TYPE,
  TaskMetadataQuestionFormat,
} from '../constants/taskConstants';
import { PHYTO_TASK_TYPE_GROUP, TASK_TYPE_STATUS } from '../constants/taskTypeConstants';
import { I18n } from '../i18n';
import { TaskType } from '../slices/taskTypes';
import { TaskMetadata } from '../slices/tasks';
import { getColorWithHash } from './colors';

export function isTaskTypeArchived(taskType: TaskType) {
  return taskType.status === TASK_TYPE_STATUS.ARCHIVED;
}

export function getCharacteristicsFromMetadata(metadata: TaskMetadata[]): Characteristic[] {
  return metadata
    .filter((metadata) => !metadata.is_instruction)
    .toSorted((a, b) => a.position - b.position)
    .map((metadata) => {
      const definedResponses = metadata.defined_responses || [];
      const type =
        metadata.response_format === TASK_METADATA_QUESTION_FORMAT.OPEN
          ? metadata.response_type === TASK_METADATA_QUESTION_TYPE.NUMBER
            ? 'number'
            : 'string'
          : metadata.response_format === TASK_METADATA_QUESTION_FORMAT.SINGLE_CLOSED
            ? 'options'
            : metadata.response_format === TASK_METADATA_QUESTION_FORMAT.MULTI_CLOSED
              ? 'options_multi'
              : metadata.response_format === TASK_METADATA_QUESTION_FORMAT.YES_OR_NO
                ? 'yes_no'
                : 'string';
      return {
        id: metadata.id,
        label: metadata.title || '',
        mandatory: metadata.mandatory || false,
        type,
        options: type === 'options' || type === 'options_multi' ? definedResponses : [''],
      };
    });
}

export function getMetadataFromCharacteristics(characteristics: Characteristic[]): TaskMetadata[] {
  return characteristics.map((characteristic, i) => ({
    id: characteristic.id,
    title: characteristic.label,
    mandatory: characteristic.mandatory,
    response_format:
      characteristic.type === 'number' || characteristic.type === 'string'
        ? TASK_METADATA_QUESTION_FORMAT.OPEN
        : characteristic.type === 'yes_no'
          ? TASK_METADATA_QUESTION_FORMAT.YES_OR_NO
          : characteristic.type === 'options_multi'
            ? TASK_METADATA_QUESTION_FORMAT.MULTI_CLOSED
            : TASK_METADATA_QUESTION_FORMAT.SINGLE_CLOSED,
    response_type:
      characteristic.type === 'number' ? TASK_METADATA_QUESTION_TYPE.NUMBER : TASK_METADATA_QUESTION_TYPE.STRING,
    defined_responses:
      characteristic.type === 'options' || characteristic.type === 'options_multi' ? characteristic.options : [],
    position: i,
  }));
}

export function formatApiTaskTypeTemplate(template: TaskType): TaskType {
  return {
    ...template,
    color: template.color ? getColorWithHash(template.color) : undefined,
    label: template.label.slice(0, 1).toLocaleUpperCase() + template.label.slice(1),
    metadata: template.metadata.map((metadata) => ({
      ...metadata,
      title: metadata.title.slice(0, 1).toLocaleUpperCase() + metadata.title.slice(1),
    })),
  };
}

export function formatApiTaskType(taskType: TaskType): TaskType {
  return {
    ...taskType,
    color: taskType.color ? getColorWithHash(taskType.color) : undefined,
    metadata: taskType.metadata || [],
    default_article_ids: taskType.default_article_ids || [],
  };
}

export function isPhytoTaskType(taskType?: TaskType) {
  return taskType ? taskType.group_by === PHYTO_TASK_TYPE_GROUP : false;
}

export function isPhytoTaskNotSeed(taskType?: TaskType) {
  return isPhytoTaskType(taskType) && taskType?.label === PHYTO_TASK_TYPE.PHYTO;
}

export function getTaskTypeLabel(taskType?: TaskType) {
  return taskType
    ? isPhytoTaskType(taskType)
      ? taskType.label === PHYTO_TASK_TYPE.PHYTO
        ? I18n.t(`PhytoTaskType.${PHYTO_TASK_TYPE.PHYTO}`)
        : taskType.label === PHYTO_TASK_TYPE.SEMENCE
          ? I18n.t(`PhytoTaskType.${PHYTO_TASK_TYPE.SEMENCE}`)
          : taskType.label
      : taskType.label
    : '';
}

/** Task type color, with a fallback */
export function getTaskTypeColor(taskType?: TaskType) {
  return taskType?.color ? getColorWithHash(taskType.color) : colors('grey', 500);
}

export function getMetadataResponseLabel(response: string, format: TaskMetadataQuestionFormat) {
  if (format === TASK_METADATA_QUESTION_FORMAT.YES_OR_NO) {
    return response === 'true'
      ? I18n.t('TaskTables.characteristics_radio_yes')
      : I18n.t('TaskTables.characteristics_radio_no');
  }
  return response;
}
